import { RFIStatus } from 'shared'
import CIMSFormItem from '../../../../../../../../../../../../../components/CIMSFormItem'
import {
  RequestForInformation,
  useRFITrackerContext,
} from '../../../../../../RFITrackerContext'
import { Col, DatePicker, Row } from 'antd'
import CIMSSelect from '../../../../../../../../../../../../../components/CIMSSelect'
import ExternalLinkEditor from '../../../../../../../../../../../../../components/ExternalLinkEditor'
import { ExternalLinkInput } from '../../../../../../../../../../../../../schema'
import { Moment } from 'moment'
import { useDisabledOptions } from '../../../../../../../../../../../../../hooks/useDisabledOptions'
import CustomFieldEditor from '../../../../../../../../../../../../../components/CustomFields/CustomFieldEditor'
import { CustomFieldEditorValues } from '../../../../../../../../../../../../../components/CustomFields/CustomFieldEditor/types'
import { HelpMessage } from '../../../../../../../../../../../../../utils/constants'
import { useRespondedResponseDisciplineOptions } from '../../../useRespondedResponseDisciplineOptions'
import CIMSTextArea from '../../../../../../../../../../../../../components/CIMSTextArea'
import { useValidateWhitespaceProps } from '../../../../../../../../../../../../../components/CIMSTextArea/hooks/useValidateWhitespaceProps'

const Help = HelpMessage.RequestsForInformation.RFITracker

export type EditRFIModalValues = {
  description: string
  responseDisciplineIds?: string[]
  organizationId?: string | null
  requestedResponseDate?: Moment | null
  externalLinks?: ExternalLinkInput[]
} & Partial<CustomFieldEditorValues>

export const EditRFIModalBody: React.FC<RequestForInformation> = ({
  statusId,
  responses,
}) => {
  const {
    metadata,
    selectOptions: { organizationOptions },
  } = useRFITrackerContext()
  const editResponseDisciplineOptions = useRespondedResponseDisciplineOptions({
    rfiResponses: responses,
    allResponseDisciplines: metadata?.rfiResponseDisciplines,
  })
  const validateWhitespaceProps = useValidateWhitespaceProps({
    label: 'Description',
    required: true,
  })
  const draft = statusId === RFIStatus.Draft.id
  const [notDraftDisabledOptions, { some: notDraftDisabled }] =
    useDisabledOptions({
      disabled: !draft,
      reason: {
        why: 'only status',
        statusName: RFIStatus.Draft.name,
      },
    })
  return (
    <>
      <CIMSFormItem
        name="description"
        disabled={notDraftDisabledOptions}
        help={HelpMessage.RequestsForInformation.RFITracker.Description}
        {...validateWhitespaceProps}
      >
        <CIMSTextArea disabled={notDraftDisabled} />
      </CIMSFormItem>
      <CIMSFormItem
        name="responseDisciplineIds"
        label="Response Disciplines"
        validateFirst={'parallel'}
        requiredMessage={
          statusId === RFIStatus.PendingResponses.id
            ? `An RFI in ${RFIStatus.PendingResponses.name} must have at least one Response Discipline`
            : undefined
        }
        customValidator={{
          validator: (value) => {
            if (statusId !== RFIStatus.PendingResponses.id) return true
            for (let disciplineId of value) {
              if (
                responses
                  .filter(
                    ({ responseDisciplineId }) =>
                      responseDisciplineId === disciplineId
                  )
                  .every(({ closed }) => !closed)
              )
                return true
            }
            return false
          },
          message: `An RFI in ${RFIStatus.PendingResponses.name} must have at least one open Response Discipline`,
        }}
        disabled={{
          disabled: statusId === RFIStatus.Closed.id,
          reason: {
            why: 'invalid status',
            statusName: RFIStatus.Closed.name,
          },
        }}
        help={Help.ResponseDisciplines}
      >
        <CIMSSelect
          simple
          multiple
          // have to do this to prevent allowClear from clearing disabled options
          allowClear={false}
          options={editResponseDisciplineOptions}
          disabled={statusId === RFIStatus.Closed.id}
        />
      </CIMSFormItem>
      <Row gutter={30}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="organizationId"
            label="Submitting Organization"
            disabled={notDraftDisabledOptions}
            help={Help.Organization}
          >
            <CIMSSelect
              simple
              options={organizationOptions}
              disabled={notDraftDisabled}
            />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="requestedResponseDate"
            label="Requested Response Date"
            disabled={notDraftDisabledOptions}
            help={Help.ResponseDate}
          >
            <DatePicker allowClear disabled={notDraftDisabled} />
          </CIMSFormItem>
        </Col>
      </Row>
      <CIMSFormItem name="externalLinks">
        <ExternalLinkEditor
          quickLinkOptions={{
            loading: metadata?.loading,
            quickLinks: metadata?.rfiQuickLinks,
          }}
        />
      </CIMSFormItem>
      <CustomFieldEditor
        alreadyDisabled={
          statusId === RFIStatus.Closed.id
            ? {
                why: 'invalid status',
                statusName: RFIStatus.Closed.name,
              }
            : false
        }
        hideBooleans={true}
      />
    </>
  )
}
